import * as Sentry from '@sentry/react';
import { UserProfile } from '../types';

// Configuration Sentry avec maximum d'informations
export const initSentry = () => {
  Sentry.init({
    dsn: 'https://86257357478bcac6ff62876eb0288e02@o4510550034022400.ingest.de.sentry.io/4510744567152720',
    
    // Environnement
    environment: (import.meta as any).env?.MODE || 'development',
    
    // Release tracking (optionnel, peut être configuré avec CI/CD)
    release: (import.meta as any).env?.VITE_APP_VERSION || undefined,
    
    // Performance monitoring
    integrations: [
      Sentry.browserTracingIntegration({
        // Suivre les navigations
        enableInp: true,
        enableLongTask: true,
      }),
      Sentry.replayIntegration({
        maskAllText: false, // Ne pas masquer le texte pour plus d'infos
        blockAllMedia: false, // Ne pas bloquer les médias
        networkDetailAllowUrls: [
          // Inclure les URLs importantes pour le debugging
          window.location.origin,
        ],
        networkCaptureBodies: true, // Capturer les corps des requêtes
      }),
      // feedbackIntegration retiré - le bouton "Bug" n'est plus affiché
    ],
    
    // Traces
    tracesSampleRate: 1.0, // 100% des transactions pour le développement
    replaysSessionSampleRate: 0.1, // 10% des sessions pour replay
    replaysOnErrorSampleRate: 1.0, // 100% des erreurs pour replay
    
    // Source maps
    attachStacktrace: true,
    
    // Informations supplémentaires
    beforeSend(event, hint) {
      // Ajouter des informations supplémentaires si disponibles
      if (hint.originalException) {
        event.extra = {
          ...event.extra,
          originalException: String(hint.originalException),
        };
      }
      
      // Ajouter des informations sur l'URL et la navigation
      if (typeof window !== 'undefined') {
        event.extra = {
          ...event.extra,
          url: window.location.href,
          pathname: window.location.pathname,
          search: window.location.search,
          referrer: document.referrer,
          userAgent: navigator.userAgent,
          language: navigator.language,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
        };
      }
      
      return event;
    },
    
    // Ignorer certaines erreurs si nécessaire
    ignoreErrors: [
      // Erreurs de réseau courantes
      'NetworkError',
      'Network request failed',
      // Erreurs de résolution DNS
      'Resolving timed out',
      // Erreurs de CORS (si non critiques)
      // 'CORS',
    ],
    
    // Tags par défaut
    initialScope: {
      tags: {
        component: 'frontend',
        framework: 'react',
        build: (import.meta as any).env?.MODE || 'development',
      },
    },
  });
  
  // Capturer les erreurs non gérées globalement
  window.addEventListener('error', (event) => {
    Sentry.captureException(event.error || new Error(event.message), {
      tags: {
        errorType: 'unhandled_error',
      },
      contexts: {
        error: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      },
    });
  });
  
  // Capturer les promesses rejetées non gérées
  window.addEventListener('unhandledrejection', (event) => {
    Sentry.captureException(event.reason, {
      tags: {
        errorType: 'unhandled_promise_rejection',
      },
    });
  });
};

// Fonction pour mettre à jour le contexte utilisateur
export const setSentryUser = (userProfile: UserProfile | null, session: any) => {
  if (userProfile && session?.user) {
    const fullName = [userProfile.firstName, userProfile.lastName].filter(Boolean).join(' ') || undefined;
    
    Sentry.setUser({
      id: userProfile.id,
      email: session.user.email || userProfile.email || undefined,
      username: fullName,
      // Informations supplémentaires dans les tags
    });
    
    // Tags utilisateur
    Sentry.setTag('user_role', userProfile.role);
    Sentry.setTag('user_access_level', userProfile.accessLevel);
    
    // Contexte supplémentaire
    Sentry.setContext('user_profile', {
      id: userProfile.id,
      email: userProfile.email,
      firstName: userProfile.firstName,
      lastName: userProfile.lastName,
      role: userProfile.role,
      accessLevel: userProfile.accessLevel,
    });
  } else {
    Sentry.setUser(null);
  }
};

// Fonction pour ajouter des tags personnalisés
export const setSentryTag = (key: string, value: string) => {
  Sentry.setTag(key, value);
};

// Fonction pour ajouter du contexte personnalisé
export const setSentryContext = (name: string, context: Record<string, any>) => {
  Sentry.setContext(name, context);
};

// Fonction pour ajouter un breadcrumb
export const addSentryBreadcrumb = (
  message: string,
  category?: string,
  level?: Sentry.SeverityLevel,
  data?: Record<string, any>
) => {
  Sentry.addBreadcrumb({
    message,
    category: category || 'custom',
    level: level || 'info',
    data,
    timestamp: Date.now() / 1000,
  });
};

// Fonction pour capturer une exception avec contexte
export const captureException = (error: Error, context?: Record<string, any>) => {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
};

// Fonction pour capturer un message
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, any>) => {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
      Sentry.captureMessage(message, level);
    });
  } else {
    Sentry.captureMessage(message, level);
  }
};
